import type { Writable } from "svelte/store";
import type { StorjConfig } from "./fs";
import { FS } from "./fs";
import { s3SanitizeDir, s3SanitizePath } from "./fs-s3";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// The browser cannot speak Storj uplink. Calls go through /api/storj, which
// uses the S3 gateway. The access key and secret are request headers.

class FileSystemStorj extends FS {
  constructor(config: StorjConfig) {
    super(config);
  }

  get storjConfig(): StorjConfig {
    return this.config as StorjConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let entries = await storjList(this.storjConfig, dirPath);
      return FsDir.fromWire(dirPath, entries);
    });
  }

  async getPublicURL(path: string): Promise<string> {
    let url = storjURL(this.storjConfig, "/api/storj/getpublicurl", "path", path);
    let js = await storjCall(this.storjConfig, url);
    if (!js.url) {
      throw new Error("storj did not return a url");
    }
    return mkURL("https://workers.filerion.com/api/fetch", "url", js.url);
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    path = s3SanitizePath(path);
    let url = storjURL(this.storjConfig, "/api/storj/upload", "path", path);
    await storjSend("PUT", url, this.storjConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = storjURL(this.storjConfig, "/api/storj/rename", "path", path, "newPath", newPath);
    await storjCall(this.storjConfig, url);
  }

  async createDirectory(path: string): Promise<void> {
    let url = storjURL(this.storjConfig, "/api/storj/createdirectory", "path", path);
    await storjCall(this.storjConfig, url);
  }

  async deleteFile(path: string): Promise<void> {
    path = s3SanitizePath(path);
    let url = storjURL(this.storjConfig, "/api/storj/deletefile", "path", path);
    await storjCall(this.storjConfig, url);
  }
}

export async function testStorjConnection(config: StorjConfig): Promise<{ bucket: string }> {
  let js = await storjCall(config, storjURL(config, "/api/storj/test"));
  return { bucket: js.bucket || config.bucket };
}

async function storjList(config: StorjConfig, dir: string): Promise<any[][]> {
  dir = s3SanitizeDir(dir);
  let js = await storjCall(config, storjURL(config, "/api/storj/list", "dir", dir));
  if (!Array.isArray(js)) {
    throw new Error("storj list failed");
  }
  return js;
}

function storjURL(config: StorjConfig, path: string, ...args: string[]): string {
  return mkURL(path, "bucket", config.bucket, "endpoint", config.endpoint, ...args);
}

function storjHeaders(config: StorjConfig): Record<string, string> {
  return {
    "X-Storj-Access": config.access,
    "X-Storj-Secret": config.secret,
  };
}

async function storjCall(config: StorjConfig, url: string): Promise<any> {
  let rsp = await fetch(url, { headers: storjHeaders(config) });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `storj ${rsp.status}`);
  }
  return js;
}

function storjSend(
  method: string,
  url: string,
  config: StorjConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
): Promise<void> {
  let headers = storjHeaders(config);
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    for (let [k, v] of Object.entries(headers)) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(storjErrText(xhr)));
    };
    xhr.onerror = () => {
      reject(new Error("storj request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      xhr.upload.onprogress = (ev) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

function storjErrText(xhr: XMLHttpRequest): string {
  let text = xhr.responseText || "";
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `storj ${xhr.status}`;
}

export function makeFileSystemStorj(config: StorjConfig): FileSystemStorj {
  return new FileSystemStorj(config);
}
